import { LBBadge, LBChevronDownIcon } from '@linearb/buzz'

import {
  FEATURE_FLAG_STATUS_LABELS,
  FeatureFlagStatus
} from '../constants/featureFlagStatus'

const STATUS_STYLES: Record<FeatureFlagStatus, string> = {
  wip: 'bg-accent text-muted-foreground border-border',
  closed_beta:
    'bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-800',
  open_beta:
    'bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800',
  ga: 'bg-green-100 text-green-700 border-green-300 dark:bg-green-950 dark:text-green-300 dark:border-green-800'
}

/**
 * Dot for dense contexts (the status picker), where a row of full pills is too
 * noisy and keeps looking clickable even when the option is disabled. Same hue
 * as the badge, one step brighter than its text colour: the badge reads as its
 * pale fill, so the text shade alone comes out far darker as a solid dot.
 */
const DOT_STYLES: Record<FeatureFlagStatus, string> = {
  wip: 'bg-muted-foreground/60',
  closed_beta: 'bg-amber-500',
  open_beta: 'bg-blue-500',
  ga: 'bg-green-500'
}

export const FeatureFlagStatusDot = ({
  status
}: {
  status: FeatureFlagStatus
}) => (
  <span
    className={`size-2 shrink-0 rounded-full ${DOT_STYLES[status]}`}
    aria-hidden='true'
  />
)

interface FeatureFlagStatusBadgeProps {
  status?: FeatureFlagStatus | null
  /** Styles the pill as a dropdown trigger: caret inside, hover affordance */
  interactive?: boolean
}

const FeatureFlagStatusBadge = ({
  status,
  interactive = false
}: FeatureFlagStatusBadgeProps) => {
  if (!status || !FEATURE_FLAG_STATUS_LABELS[status]) {
    return <span className='text-muted-foreground'>—</span>
  }

  return (
    <LBBadge
      variant='outline'
      className={`text-xs whitespace-nowrap justify-center gap-1 ${STATUS_STYLES[status]} ${
        interactive ? 'cursor-pointer hover:brightness-95' : ''
      }`}
    >
      {FEATURE_FLAG_STATUS_LABELS[status]}
      {interactive && <LBChevronDownIcon className='size-3 opacity-60' />}
    </LBBadge>
  )
}

export default FeatureFlagStatusBadge
